export const findActivePlanSession = (activePlan, sessionId) => {
  if (!activePlan?.weeks || !sessionId) return null;
  for (const week of activePlan.weeks) {
    for (const session of week.sessions || []) {
      if (session.session_id === sessionId) return session;
    }
  }
  return null;
};

export const enrichCoachSession = (summarySession, activePlan) => {
  if (!summarySession) return null;
  const full = findActivePlanSession(activePlan, summarySession.session_id);
  if (!full) return summarySession;
  return {
    ...summarySession,
    ...full,
    planned_start_time: summarySession.planned_start_time ?? full.planned_start_time,
    intensity_target_summary:
      summarySession.intensity_target_summary ?? full.intensity_summary,
  };
};

const sessionDateKey = (session) => String(session?.scheduled_date || "").slice(0, 10);

const dateKey = (value) => String(value || "").slice(0, 10);

/** Workout rows plus calendar rest days, in date order. Rest is not a workout. */
export const planDatedRows = (week, sessions) => {
  const rows = (sessions || []).map((session) => ({
    kind: "session",
    date: sessionDateKey(session),
    session,
  }));
  const taken = new Set(rows.map((row) => row.date).filter(Boolean));
  for (const value of week?.rest_dates || []) {
    const date = dateKey(value);
    if (!date || taken.has(date)) continue;
    taken.add(date);
    rows.push({ kind: "rest", date });
  }
  rows.sort((left, right) => left.date.localeCompare(right.date));
  return rows;
};

export const activePlanRestDateKeys = (activePlan) => {
  const keys = new Set();
  for (const week of activePlan?.weeks || []) {
    for (const value of week.rest_dates || []) {
      const date = dateKey(value);
      if (date) keys.add(date);
    }
  }
  return keys;
};

export const activePlanUpcomingSessions = (
  activePlan,
  { todayDate = null, todaySessionId = null, fallbackUpcoming = [] } = {},
) => {
  const planSessions = (activePlan?.weeks || []).flatMap((week) => week.sessions || []);
  if (planSessions.length === 0) {
    return fallbackUpcoming;
  }

  const today = todayDate ? String(todayDate).slice(0, 10) : null;

  return planSessions
    .filter((session) => {
      if (session.is_completed) return false;
      if (session.is_actionable === false) return false;
      const date = sessionDateKey(session);
      if (!date) return false;
      if (today && date < today) return false;
      if (todaySessionId && session.session_id === todaySessionId) return false;
      if (today && date === today) return false;
      return true;
    })
    .sort((left, right) => sessionDateKey(left).localeCompare(sessionDateKey(right)));
};

export const activePlanBlockDates = (activePlan, block, timezone) => {
  const sessions = (activePlan?.weeks || []).flatMap((week) => week.sessions || []);
  const scheduledDates = sessions
    .map((session) => session?.scheduled_date)
    .filter(Boolean)
    .sort();
  const finalWorkoutDate = scheduledDates[scheduledDates.length - 1] || null;
  const start = activePlan?.block_start_date
    ?? block?.start_date
    ?? scheduledDates[0]
    ?? null;
  const end = activePlan?.block_end_date
    ?? block?.end_date
    ?? finalWorkoutDate;
  return { start, end, finalWorkoutDate, timezone };
};

const calendarDay = (value) => {
  if (!value) return null;
  const text = String(value).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(text) ? text : null;
};

/**
 * Athlete-facing plan period. Database activation can be current before
 * the start date. The end date itself is still inside the plan.
 */
export const trainingPlanPeriodStatus = ({ today, start, end } = {}) => {
  const todayDay = calendarDay(today);
  const startDay = calendarDay(start);
  const endDay = calendarDay(end);
  if (todayDay && startDay && todayDay < startDay) return "Upcoming";
  if (todayDay && endDay && todayDay > endDay) return "Ended";
  return "Active";
};

export const activePlanWeekLabel = (activePlan, block, week) => {
  const currentWeekNumber =
    activePlan?.current_week_number
    ?? block?.current_week_number
    ?? week?.week_number;
  const totalWeeks = activePlan?.weeks?.length ?? block?.total_weeks;
  if (currentWeekNumber && totalWeeks) {
    return `Week ${currentWeekNumber} of ${totalWeeks}`;
  }
  if (activePlan?.current_week_number) {
    return `Week ${activePlan.current_week_number}`;
  }
  if (week?.week_number) {
    return `Week ${week.week_number}`;
  }
  return null;
};
